/**
 * Channel reply-button count limits (aligned with backend ConversationInteractiveButtonLimits).
 * `null` means no platform count limit.
 */
export type InteractiveButtonChannel =
  | 'whatsapp'
  | 'instagram'
  | 'messenger'
  | 'telegram'
  | 'webchat'
  | 'tiktok'
  | string;

export const INTERACTIVE_BUTTON_DEFAULT_MAX = 3;
export const INTERACTIVE_BUTTON_WHATSAPP_MAX = 3;
export const INTERACTIVE_BUTTON_INSTAGRAM_MAX = 13;
export const INTERACTIVE_BUTTON_MESSENGER_MAX = 3;

export const getInteractiveButtonMaxCountForChannel = (
  channel: InteractiveButtonChannel | null | undefined,
): number | null => {
  switch ((channel ?? '').trim().toLowerCase()) {
    case 'whatsapp':
      return INTERACTIVE_BUTTON_WHATSAPP_MAX;
    case 'instagram':
      return INTERACTIVE_BUTTON_INSTAGRAM_MAX;
    case 'messenger':
      return INTERACTIVE_BUTTON_MESSENGER_MAX;
    case 'telegram':
    case 'webchat':
      return null;
    default:
      return INTERACTIVE_BUTTON_DEFAULT_MAX;
  }
};

/** Most restrictive finite limit; unlimited channels are ignored when any finite limit exists. */
export const resolveInteractiveButtonMaxCount = (
  channels: Array<InteractiveButtonChannel | null | undefined>,
): number | null => {
  const finite = channels
    .map(getInteractiveButtonMaxCountForChannel)
    .filter((max): max is number => typeof max === 'number');

  if (finite.length === 0) {
    const hasKnownUnlimited = channels.some(channel => {
      const normalized = (channel ?? '').trim().toLowerCase();
      return normalized === 'telegram' || normalized === 'webchat';
    });
    return hasKnownUnlimited ? null : INTERACTIVE_BUTTON_DEFAULT_MAX;
  }

  return Math.min(...finite);
};

export const canAddInteractiveButton = (
  currentCount: number,
  maxCount: number | null | undefined,
): boolean => maxCount == null || currentCount < maxCount;

export const isInteractiveButtonAddDisabled = (
  currentCount: number,
  maxCount: number | null | undefined,
): boolean => !canAddInteractiveButton(currentCount, maxCount);
